import React, { FC, useContext, useEffect } from 'react';
import { useHistory, useLocation } from 'react-router-dom';

import { EFeaturedFlags } from 'types/EFeatureFlags';
import { useFeaturedFlagEnabled } from 'helpers/posthogHooks';
import { ChannelsTab, EveryoneTab, ExploreTab, FollowingTab } from 'components/Explore';
import { EExploreTabs } from 'components/Explore/ExploreTabs/helpers/constants';
import ExploreTabs from 'components/Explore/ExploreTabs';
import useDiscoveryTab from 'helpers/useDiscoveryTab';
import StyledWrap from 'components/UI/Wrap';
import { LAST_STEP } from 'components/GuideTour/constants';

import useTheme from 'helpers/useTheme';
import useDocumentTitle from 'helpers/useDocumentTitle';

import { IFollowingKey } from 'providers/helpers/models';
import { FollowingTabKeyContext } from 'providers/helpers/contexts';

import { ROUTE_DISCOVER } from 'routes';
import { getTabFromPath, DISCOVER_DEFAULT_PATH, DISCOVER_DEFAULT_TAB } from './helpers';

const ExplorePage: FC = () => {
  const { followingTabKey } = useContext<IFollowingKey>(FollowingTabKeyContext);
  const isDark = useTheme();
  const { discoveryTab, setDiscoveryTab } = useDiscoveryTab();
  const { pathname } = useLocation();
  const { replace } = useHistory();

  useDocumentTitle();

  const isShowChannelsTab = useFeaturedFlagEnabled(EFeaturedFlags.ShowChannelsTab);
  const isShowEveryOneTab = useFeaturedFlagEnabled(EFeaturedFlags.ShowEveryoneTab);

  // Resolve the tab from the URL. Redirect bare /discover, unknown paths,
  // and feature-flag-disabled tabs to the default (/discover/following).
  useEffect(() => {
    if (pathname === ROUTE_DISCOVER) {
      replace(DISCOVER_DEFAULT_PATH);
      return;
    }

    const tabFromPath = getTabFromPath(pathname);

    if (tabFromPath === null) {
      replace(DISCOVER_DEFAULT_PATH);
      return;
    }

    if (tabFromPath === EExploreTabs.Channels && !isShowChannelsTab) {
      replace(DISCOVER_DEFAULT_PATH);
      return;
    }

    if (tabFromPath === EExploreTabs.Everyone && !isShowEveryOneTab) {
      replace(DISCOVER_DEFAULT_PATH);
      return;
    }

    if (discoveryTab !== tabFromPath) {
      setDiscoveryTab?.(tabFromPath);
    }
  }, [pathname, isShowChannelsTab, isShowEveryOneTab, discoveryTab, replace, setDiscoveryTab]);

  // Render whichever tab the URL resolved to, falling back to the default
  // for the one frame before the effect runs (or between redirect + remount).
  const activeTab = getTabFromPath(pathname) ?? DISCOVER_DEFAULT_TAB;

  return (
    <StyledWrap data-tour={LAST_STEP} paddingTop={16}>
      <ExploreTabs isDark={isDark} />
      {activeTab === EExploreTabs.Everyone && isShowEveryOneTab && <EveryoneTab />}
      {activeTab === EExploreTabs.Following && <FollowingTab key={followingTabKey} />}
      {activeTab === EExploreTabs.Featured && <ExploreTab />}
      {activeTab === EExploreTabs.Channels && isShowChannelsTab && <ChannelsTab />}
    </StyledWrap>
  );
};

export default ExplorePage;
